import { useEffect, useRef } from 'react';

import type { NoharaRepresentative } from '../../types/company.types';
import type { ValidationError } from '../../utils/validations';

/**
 * Common hook for marking required fields as touched on form load.
 * This hook is used across customer, supplier, and construction registration forms.
 */

interface UseMarkRequiredFieldsTouchedProps {
  touchedFields: Set<string>;
  setTouchedFields: (fields: Set<string>) => void;
  representatives: NoharaRepresentative[];
  /**
   * Either provide validation errors to mark fields with errors as touched,
   * or provide a list of basic required field names to mark directly
   */
  fieldsToMark?: string[] | ValidationError[];
  /**
   * If true, fieldsToMark is treated as ValidationError[] and field names are extracted from errors
   * If false or undefined, fieldsToMark is treated as string[] of field names
   */
  useValidationErrors?: boolean;
}

/**
 * Common hook for marking required fields as touched on form load
 * Handles both validation-error-based marking and direct field name marking
 * Always marks all representative fields as touched
 */
export const useMarkRequiredFieldsTouched = ({
  touchedFields,
  setTouchedFields,
  representatives,
  fieldsToMark,
  useValidationErrors = false,
}: UseMarkRequiredFieldsTouchedProps) => {
  const hasMarkedRequiredFields = useRef(false);

  useEffect(() => {
    // Always preserve existing touched fields and ensure required fields are marked
    const newTouchedFields = new Set(touchedFields);
    let hasChanges = false;

    // Mark basic required fields as touched (either from validation errors or direct field list)
    if (fieldsToMark && fieldsToMark.length > 0) {
      const fieldsToAdd = useValidationErrors
        ? (fieldsToMark as ValidationError[]).map((error) => error.field)
        : (fieldsToMark as string[]);

      fieldsToAdd.forEach((field) => {
        if (!newTouchedFields.has(field)) {
          newTouchedFields.add(field);
          hasChanges = true;
        }
      });
    }

    // Always mark representative fields as touched when representatives change
    representatives.forEach((_, index) => {
      const deptField = `representatives.${index}.department`;
      const salesField = `representatives.${index}.salesPerson`;

      if (!newTouchedFields.has(deptField)) {
        newTouchedFields.add(deptField);
        hasChanges = true;
      }
      if (!newTouchedFields.has(salesField)) {
        newTouchedFields.add(salesField);
        hasChanges = true;
      }
    });

    // Update if there are new fields to add
    if (hasChanges) {
      setTouchedFields(newTouchedFields);
      hasMarkedRequiredFields.current = true;
    } else if (!hasMarkedRequiredFields.current) {
      // Even if no changes, mark as done to prevent unnecessary re-runs
      hasMarkedRequiredFields.current = true;
    }
  }, [representatives, setTouchedFields, touchedFields, fieldsToMark, useValidationErrors]);

  return hasMarkedRequiredFields;
};
